import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../../components/admin-layout';
import { Card, Button, Table, Th, Td } from '../../components/ui';
import {
  getAbsensiStatus,
  getAbsensiToday,
  checkIn,
  checkOut,
  getRiwayatAbsensi,
  getRekapAbsensi,
  getPeriodeList,
} from '../../features/absensi/api';
import { getSekolahList } from '../../features/master/api';
import type {
  AbsensiStatus,
  Absensi,
  RiwayatAbsensiItem,
  RekapAbsensiRow,
  Periode,
  Sekolah,
  StatusKehadiran,
} from '@app/shared';
import { ApiClientError } from '../../lib/api';
import { useAuth } from '../../features/auth/use-auth';
import { exportRekapAbsensiXlsx } from '../../features/settings/export';

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  hadir: { bg: '#DCFCE7', text: '#15803D', label: 'Hadir' },
  izin: { bg: '#FEF9C3', text: '#A16207', label: 'Izin' },
  sakit: { bg: '#DBEAFE', text: '#1D4ED8', label: 'Sakit' },
  alpha: { bg: '#FEE2E2', text: '#B91C1C', label: 'Alpha' },
};

function StatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return <span style={{ color: '#64748B', fontSize: '0.875rem' }}>-</span>;
  const c = STATUS_COLORS[status] ?? { bg: '#F1F5F9', text: '#475569', label: status };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '0.125rem 0.625rem',
        borderRadius: '0.25rem',
        fontSize: '0.75rem',
        fontWeight: 600,
        background: c.bg,
        color: c.text,
        textTransform: 'uppercase',
      }}
    >
      {c.label}
    </span>
  );
}

type TabKey = 'absensi' | 'riwayat' | 'rekap';

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '0.5rem 1rem',
        border: 'none',
        background: active ? '#0F172A' : 'transparent',
        color: active ? '#FFFFFF' : '#475569',
        fontWeight: 600,
        fontSize: '0.875rem',
        cursor: 'pointer',
        borderRadius: '0.375rem',
      }}
    >
      {children}
    </button>
  );
}

// --- Tab 1: Absensi Hari Ini (existing) ---
function AbsensiTab() {
  const [status, setStatus] = useState<AbsensiStatus | null>(null);
  const [todayData, setTodayData] = useState<Absensi[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const [statusData, todayRes] = await Promise.all([getAbsensiStatus(), getAbsensiToday()]);
      setStatus(statusData);
      setTodayData(todayRes.data ?? []);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Gagal memuat data absensi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleCheckIn = async () => {
    setActionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await checkIn();
      setSuccess('Check In berhasil');
      await fetchStatus();
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Gagal melakukan Check In');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setActionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await checkOut();
      setSuccess('Check Out berhasil');
      await fetchStatus();
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Gagal melakukan Check Out');
    } finally {
      setActionLoading(false);
    }
  };

  const sudahCheckIn = status?.absensi?.sudahCheckIn ?? false;
  const sudahCheckOut = status?.absensi?.sudahCheckOut ?? false;
  const periodeAktif = status?.periodeAktif ?? null;
  const absensiStatus = status?.absensi?.status ?? null;
  const checkInAt = status?.absensi?.checkInAt ?? null;
  const checkOutAt = status?.absensi?.checkOutAt ?? null;

  if (loading) {
    return <div style={{ color: '#64748B' }}>Memuat data absensi...</div>;
  }

  return (
    <>
      {error && (
        <div
          style={{
            background: '#FEE2E2',
            color: '#B91C1C',
            padding: '0.75rem 1rem',
            borderRadius: '0.375rem',
            marginBottom: '1rem',
            fontSize: '0.875rem',
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            background: '#DCFCE7',
            color: '#15803D',
            padding: '0.75rem 1rem',
            borderRadius: '0.375rem',
            marginBottom: '1rem',
            fontSize: '0.875rem',
          }}
        >
          {success}
        </div>
      )}

      <Card style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase' }}>
              Nama Pegawai
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 600 }}>{status?.pegawai?.nama ?? '-'}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase' }}>
              NIP/NIK
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 600 }}>
              {status?.pegawai?.nipNik ?? '-'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase' }}>
              Tanggal
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 600 }}>{status?.tanggal ?? '-'}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase' }}>
              Jam Server
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 600 }}>{status?.serverTime ?? '-'}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase' }}>
              Periode Aktif
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 600 }}>
              {periodeAktif
                ? `${periodeAktif.nama} (${periodeAktif.kodePeriode})`
                : 'Tidak ada periode aktif'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase' }}>
              Status Kehadiran
            </div>
            <div style={{ marginTop: '0.25rem' }}>
              <StatusBadge status={absensiStatus} />
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase' }}>
              Waktu Check In
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 600 }}>{checkInAt ?? '-'}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase' }}>
              Waktu Check Out
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 600 }}>{checkOutAt ?? '-'}</div>
          </div>
        </div>
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
          <Button
            onClick={handleCheckIn}
            disabled={sudahCheckIn || !periodeAktif || actionLoading}
            variant={sudahCheckIn ? 'secondary' : 'primary'}
          >
            {actionLoading ? 'Memproses...' : 'Check In'}
          </Button>
          <Button
            onClick={handleCheckOut}
            disabled={!sudahCheckIn || sudahCheckOut || !periodeAktif || actionLoading}
            variant={!sudahCheckIn || sudahCheckOut ? 'secondary' : 'primary'}
          >
            {actionLoading ? 'Memproses...' : 'Check Out'}
          </Button>
        </div>
      </Card>

      <Card>
        <h2
          style={{
            fontSize: '1.125rem',
            fontWeight: 600,
            marginBottom: '1rem',
            color: '#0F172A',
          }}
        >
          Absensi Hari Ini
        </h2>
        {todayData.length === 0 ? (
          <div style={{ color: '#64748B', fontSize: '0.875rem', padding: '1rem 0' }}>
            Belum ada data absensi hari ini.
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Tanggal</Th>
                <Th>Status</Th>
                <Th>Check In</Th>
                <Th>Check Out</Th>
              </tr>
            </thead>
            <tbody>
              {todayData.map((item) => (
                <tr key={item.id}>
                  <Td>{item.tanggal}</Td>
                  <Td>
                    <StatusBadge status={item.status} />
                  </Td>
                  <Td>{item.checkInAt}</Td>
                  <Td>{item.checkOutAt ?? '-'}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

// --- Tab 2: Riwayat Absensi ---
function RiwayatTab() {
  const [rows, setRows] = useState<RiwayatAbsensiItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [periodeId, setPeriodeId] = useState<number | ''>('');
  const [sekolahId, setSekolahId] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<StatusKehadiran | ''>('');
  const [search, setSearch] = useState('');

  const [periodeOptions, setPeriodeOptions] = useState<Periode[]>([]);
  const [sekolahOptions, setSekolahOptions] = useState<Sekolah[]>([]);

  const { user } = useAuth();
  const isAdmin = user?.roles.includes('admin') ?? false;
  const isKS = user?.roles.includes('kepala_sekolah') ?? false;

  const fetchOptions = useCallback(async () => {
    try {
      const [periodeRes, sekolahRes] = await Promise.all([
        getPeriodeList({ page: 1, limit: 100 }),
        isAdmin
          ? getSekolahList({ page: 1, limit: 100 })
          : Promise.resolve({
              rows: [] as Sekolah[],
              meta: { page: 1, limit: 100, total: 0, total_pages: 0 },
            }),
      ]);
      setPeriodeOptions(periodeRes.rows);
      setSekolahOptions(sekolahRes.rows);
    } catch {
      // Ignore option loading errors
    }
  }, [isAdmin]);

  const fetchRiwayat = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getRiwayatAbsensi({
        page,
        limit,
        periodeId: periodeId || undefined,
        sekolahId: sekolahId || undefined,
        status: statusFilter || undefined,
        search: search || undefined,
      });
      setRows(result.rows);
      setTotal(result.total);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Gagal memuat riwayat absensi');
    } finally {
      setLoading(false);
    }
  }, [page, limit, periodeId, sekolahId, statusFilter, search]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  useEffect(() => {
    fetchRiwayat();
  }, [fetchRiwayat]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <>
      <Card style={{ marginBottom: '1rem' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '0.75rem',
            marginBottom: '0.75rem',
          }}
        >
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: 600,
                marginBottom: '0.25rem',
                color: '#475569',
              }}
            >
              Periode
            </label>
            <select
              value={periodeId}
              onChange={(e) => {
                setPeriodeId(e.target.value ? Number(e.target.value) : '');
                setPage(1);
              }}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #CBD5E1',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
              }}
            >
              <option value="">Semua Periode</option>
              {periodeOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nama}
                </option>
              ))}
            </select>
          </div>
          {isAdmin && (
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  marginBottom: '0.25rem',
                  color: '#475569',
                }}
              >
                Sekolah
              </label>
              <select
                value={sekolahId}
                onChange={(e) => {
                  setSekolahId(e.target.value ? Number(e.target.value) : '');
                  setPage(1);
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #CBD5E1',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                }}
              >
                <option value="">Semua Sekolah</option>
                {sekolahOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nama}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: 600,
                marginBottom: '0.25rem',
                color: '#475569',
              }}
            >
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as StatusKehadiran | '');
                setPage(1);
              }}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #CBD5E1',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
              }}
            >
              <option value="">Semua Status</option>
              <option value="hadir">Hadir</option>
              <option value="izin">Izin</option>
              <option value="sakit">Sakit</option>
              <option value="alpha">Alpha</option>
            </select>
          </div>
          {(isAdmin || isKS) && (
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  marginBottom: '0.25rem',
                  color: '#475569',
                }}
              >
                Pencarian
              </label>
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Nama/NIP/Sekolah..."
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #CBD5E1',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                }}
              />
            </div>
          )}
        </div>
      </Card>

      <Card>
        {error && (
          <div
            style={{
              background: '#FEE2E2',
              color: '#B91C1C',
              padding: '0.75rem 1rem',
              borderRadius: '0.375rem',
              marginBottom: '1rem',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </div>
        )}
        {loading ? (
          <div style={{ color: '#64748B', fontSize: '0.875rem', padding: '1rem 0' }}>
            Memuat riwayat...
          </div>
        ) : rows.length === 0 ? (
          <div style={{ color: '#64748B', fontSize: '0.875rem', padding: '1rem 0' }}>
            Tidak ada data riwayat absensi.
          </div>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Tanggal</Th>
                  <Th>Hari</Th>
                  <Th>Sekolah</Th>
                  {(isAdmin || isKS) && <Th>Pegawai</Th>}
                  <Th>Status</Th>
                  <Th>Check In</Th>
                  <Th>Check Out</Th>
                  <Th>Keterangan</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <tr key={item.id}>
                    <Td>{item.tanggal}</Td>
                    <Td>{item.hari}</Td>
                    <Td>{item.sekolahNama}</Td>
                    {(isAdmin || isKS) && (
                      <Td>
                        <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                          {item.pegawaiNama}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#64748B' }}>
                          {item.pegawaiNipNik}
                        </div>
                      </Td>
                    )}
                    <Td>
                      <StatusBadge status={item.status} />
                    </Td>
                    <Td>{item.checkInAt ?? '-'}</Td>
                    <Td>{item.checkOutAt ?? '-'}</Td>
                    <Td>{item.keterangan ?? '-'}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '1rem',
                fontSize: '0.875rem',
                color: '#475569',
              }}
            >
              <div>
                Menampilkan {rows.length} dari {total} data
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <Button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  variant="secondary"
                >
                  Sebelumnya
                </Button>
                <span>
                  Halaman {page} dari {totalPages}
                </span>
                <Button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  variant="secondary"
                >
                  Berikutnya
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </>
  );
}

// --- Tab 3: Rekap Absensi ---
function RekapTab() {
  const [rows, setRows] = useState<RekapAbsensiRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [periodeId, setPeriodeId] = useState<number | ''>('');
  const [sekolahId, setSekolahId] = useState<number | ''>('');
  const [search, setSearch] = useState('');

  const [periodeOptions, setPeriodeOptions] = useState<Periode[]>([]);
  const [sekolahOptions, setSekolahOptions] = useState<Sekolah[]>([]);

  const { user } = useAuth();
  const isAdmin = user?.roles.includes('admin') ?? false;
  const isKS = user?.roles.includes('kepala_sekolah') ?? false;

  const fetchOptions = useCallback(async () => {
    try {
      const [periodeRes, sekolahRes] = await Promise.all([
        getPeriodeList({ page: 1, limit: 100 }),
        isAdmin
          ? getSekolahList({ page: 1, limit: 100 })
          : Promise.resolve({
              rows: [] as Sekolah[],
              meta: { page: 1, limit: 100, total: 0, total_pages: 0 },
            }),
      ]);
      setPeriodeOptions(periodeRes.rows);
      setSekolahOptions(sekolahRes.rows);
    } catch {
      // Ignore
    }
  }, [isAdmin]);

  const fetchRekap = useCallback(async () => {
    if (!periodeId) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await getRekapAbsensi({
        periodeId,
        sekolahId: sekolahId || undefined,
        search: search || undefined,
      });
      setRows(result);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Gagal memuat rekap absensi');
    } finally {
      setLoading(false);
    }
  }, [periodeId, sekolahId, search]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  useEffect(() => {
    fetchRekap();
  }, [fetchRekap]);

  return (
    <>
      <Card style={{ marginBottom: '1rem' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '0.75rem',
            marginBottom: '0.75rem',
          }}
        >
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: 600,
                marginBottom: '0.25rem',
                color: '#475569',
              }}
            >
              Periode <span style={{ color: '#B91C1C' }}>*</span>
            </label>
            <select
              value={periodeId}
              onChange={(e) => setPeriodeId(e.target.value ? Number(e.target.value) : '')}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #CBD5E1',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
              }}
            >
              <option value="">Pilih Periode</option>
              {periodeOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nama}
                </option>
              ))}
            </select>
          </div>
          {isAdmin && (
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  marginBottom: '0.25rem',
                  color: '#475569',
                }}
              >
                Sekolah
              </label>
              <select
                value={sekolahId}
                onChange={(e) => setSekolahId(e.target.value ? Number(e.target.value) : '')}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #CBD5E1',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                }}
              >
                <option value="">Semua Sekolah</option>
                {sekolahOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nama}
                  </option>
                ))}
              </select>
            </div>
          )}
          {(isAdmin || isKS) && (
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  marginBottom: '0.25rem',
                  color: '#475569',
                }}
              >
                Pencarian
              </label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nama/NIP..."
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #CBD5E1',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                }}
              />
            </div>
          )}
        </div>
      </Card>

      <Card>
        {error && (
          <div
            style={{
              background: '#FEE2E2',
              color: '#B91C1C',
              padding: '0.75rem 1rem',
              borderRadius: '0.375rem',
              marginBottom: '1rem',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </div>
        )}
        {!periodeId ? (
          <div style={{ color: '#64748B', fontSize: '0.875rem', padding: '1rem 0' }}>
            Silakan pilih periode untuk melihat rekap.
          </div>
        ) : loading ? (
          <div style={{ color: '#64748B', fontSize: '0.875rem', padding: '1rem 0' }}>
            Memuat rekap...
          </div>
        ) : rows.length === 0 ? (
          <div style={{ color: '#64748B', fontSize: '0.875rem', padding: '1rem 0' }}>
            Tidak ada data rekap untuk periode ini.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
              <button
                type="button"
                onClick={() => {
                  void exportRekapAbsensiXlsx(rows, `rekap-absensi-${periodeId}.xlsx`);
                }}
                style={{
                  background: '#047857',
                  color: '#FFFFFF',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Export Excel
              </button>
            </div>
            <Table>
              <thead>
                <tr>
                  <Th>Nama Pegawai</Th>
                  <Th>NIP/NIK</Th>
                  <Th>Sekolah</Th>
                  <Th>Total Hadir</Th>
                  <Th>Total Izin</Th>
                  <Th>Total Sakit</Th>
                  <Th>Total Alpha</Th>
                  <Th>Total Hari Kerja</Th>
                  <Th>Persentase Kehadiran</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <tr key={item.pegawaiSekolahId}>
                    <Td style={{ fontWeight: 500 }}>{item.nama}</Td>
                    <Td>{item.nipNik}</Td>
                    <Td>{item.sekolahNama}</Td>
                    <Td>{item.hadir}</Td>
                    <Td>{item.izin}</Td>
                    <Td>{item.sakit}</Td>
                    <Td>{item.alpha}</Td>
                    <Td>{item.totalHariKerja}</Td>
                    <Td>{item.persentaseKehadiran}%</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Card>
    </>
  );
}

// --- Main Page ---
export function AbsensiPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('absensi');

  return (
    <AdminLayout title="Absensi">
      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1.5rem',
          background: '#F8FAFC',
          padding: '0.25rem',
          borderRadius: '0.5rem',
          width: 'fit-content',
        }}
      >
        <TabButton active={activeTab === 'absensi'} onClick={() => setActiveTab('absensi')}>
          Absensi
        </TabButton>
        <TabButton active={activeTab === 'riwayat'} onClick={() => setActiveTab('riwayat')}>
          Riwayat
        </TabButton>
        <TabButton active={activeTab === 'rekap'} onClick={() => setActiveTab('rekap')}>
          Rekap
        </TabButton>
      </div>

      {activeTab === 'absensi' && <AbsensiTab />}
      {activeTab === 'riwayat' && <RiwayatTab />}
      {activeTab === 'rekap' && <RekapTab />}
    </AdminLayout>
  );
}
